import { useCallback } from 'react';
import { useDeleteSessionContributor } from '@/state/mutations/sessions/use-delete-session-contributor';
import { useDeleteFlowContributor } from '@/state/mutations/flows/use-delete-flow-contributor';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';

type DeleteEntityContributorParams = {
  contributor: ContributorDto;
  entityType: 'Session' | 'Flow';
  entityId: string;
};

export const useDeleteEntityContributor = () => {
  const { mutateAsync: deleteSessionContributor } = useDeleteSessionContributor();
  const { mutateAsync: deleteFlowContributor } = useDeleteFlowContributor();
  const notification = useNotifications();
  const t = useTranslations();

  return useCallback(
    async ({ contributor, entityType, entityId }: DeleteEntityContributorParams) => {
      const displayName =
        contributor.profiles[0]?.displayName || contributor.email || t('event.detail.contributors.unknownContributor');

      try {
        // Event crew roles use DELETE /contributors/:id/roles. Session/Flow removal is handled by the entity
        // endpoints (they soft-delete the role and subsidiary slots on the contributor).
        if (entityType === 'Session') {
          await deleteSessionContributor({
            sessionId: entityId,
            contributorId: contributor.id,
          });
          notification.notifySuccess(t('session.contributors.removeSuccess', { name: displayName }));
        } else if (entityType === 'Flow') {
          await deleteFlowContributor({
            flowId: entityId,
            contributorId: contributor.id,
          });
          notification.notifySuccess(t('flows.detail.contributors.removeSuccess', { name: displayName }));
        }
      } catch (error) {
        const errorMessage =
          entityType === 'Session'
            ? t('session.contributors.removeError', { name: displayName })
            : t('flows.detail.contributors.removeError', { name: displayName });
        notification.notifyError(errorMessage);
        throw error;
      }
    },
    [deleteSessionContributor, deleteFlowContributor, notification, t],
  );
};
